<template>
  <div class="task-history-table">
    <component :is="'style'" v-html="dynamicTreeStyles"></component>
    <thux-table
      :instance="instance"
      :instance-name="instanceName"
      :order-by-dict="orderByDict"
      :goBack="goBack"
      :list="displayList"
      :fieldName="fieldName"
      :fields="fields"
      :fields-column="fieldsColumn"
      :select-all="selectAll"
      :selected-list="selectedList"
      :get-local-selected-all="getLocalSelectedAll"
      :local-selected-list="localSelectedList"
      :tbody-tr-class="tbodyTrClassFn"
      @check-all="checkAll('id')"
      @check-none="checkNone('id')"
      @set-selected-row="setSelectedRow($event, 'id')"
      :show-checks="showChecks"
      :showDetailButton="showDetailButton"
      @change-status="changeInstanceStatus"
      @sort-changed="orderTable"
      @remove-order-by="removeOrderFromTable"
      @open-detail-form="openTaskDetail"
    >
    <template #expander="{ item }">
      <button
        v-if="isTreeMode && item.has_children"
        type="button"
        class="task-tree-toggle"
        :class="{ 'task-tree-toggle-nested': item._level > 0 }"
        :title="isExpanded(item) ? $t('Collapse row') : $t('Expand row')"
        @click.stop="toggleRow(item)"
      >
        <i v-if="isLoading(item)" class="fas fa-spinner fa-spin" />
        <template v-else-if="item._level > 0">
          <i v-if="isExpanded(item)" class="fas fa-minus-square" />
          <i v-else class="fas fa-plus-square" />
        </template>
        <template v-else>
          <i v-if="isExpanded(item)" class="fas fa-caret-down" />
          <i v-else class="fas fa-caret-right" />
        </template>
      </button>
      <i
        v-else-if="isTreeMode && item._level > 0"
        class="fas fa-level-down-alt task-tree-child-hint"
        :title="$t('Sub-task')"
        aria-hidden="true"
      />
    </template>
      <template #name="{ item }">
        <span :title="item.name">{{ item.name }}</span>
      </template>
      <template #priority="{ item }">
        <span
          class="badge task-priority-chip"
          :style="getPriorityChipStyle(item)"
        >
          {{ item.priority_name }}
        </span>
      </template>
      <template #state="{ item }">
        <span
          class="badge task-state-chip"
          :style="getStateChipStyle(item)"
        >
          {{ item.state_name }}
        </span>
      </template>
      <template #quotation="{ item }">
        <span>
          {{ item.quotation_detail ? item.quotation_detail.label : '' }}
        </span>
      </template>
      <template #tags__name="{ item }">
        <span
          v-for="tag in (item.tags_detail || [])"
          :key="tag.id"
          class="badge task-tag-chip mr-1"
          :style="tag.color ? { backgroundColor: tag.color, color: '#fff' } : {}"
        >
          {{ tag.name }}
        </span>
      </template>
      <template #comments_count="{ item }">
        <div class="task-comments-cell">
          <button
            type="button"
            class="task-comments-cell__pill"
            :class="{ 'task-comments-cell__pill--empty': !item.comments_count }"
            :title="$t('Open comments')"
            @click.stop="onCommentsClick(item)"
          >
            <i class="fas fa-comment-dots" />{{ item.comments_count || 0 }}
          </button>
        </div>
      </template>
      <template #other-buttons-actions-top="{ item }">
        <b-btn
          v-if="!item.parent"
          variant="outline-primary"
          size="sm"
          class="mr-1"
          :title="$t('Create subtask')"
          @click.stop="onCreateSubtaskClick(item)"
        >
          <i class="fas fa-sitemap"></i>
        </b-btn>
        <b-btn
          v-if="canTerminate(item)"
          variant="outline-success"
          size="sm"
          class="mr-1"
          :title="$t('Terminate task')"
          @click.stop="onTerminateClick(item)"
        >
          <i class="fas fa-flag-checkered"></i>
        </b-btn>
      </template>
      <template #other-buttons-actions-bottom="{ item }">
        <b-btn
          v-if="canTerminate(item)"
          variant="outline-success"
          size="sm"
          class="mr-1"
          :title="$t('Delete task')"
          @click.stop="onDeleteClick(item)"
        >
          <i class="fas fa-trash"></i>
        </b-btn>
      </template>
    </thux-table>
    <task-terminate-modal
      v-if="taskToTerminate"
      :task="taskToTerminate"
      @terminated="onTerminated"
      @close="taskToTerminate = null"
    />
    <task-comment-modal
      v-if="taskForComments"
      :task="taskForComments"
      @close="taskForComments = null"
    />
    <task-open-subtasks-modal
      v-if="taskWithOpenSubtasks"
      :task-id="taskWithOpenSubtasks.id"
      @close="taskWithOpenSubtasks = null"
    />
    <task-delete-modal
      v-if="taskToDelete"
      :task="taskToDelete"
      :subtasks="(childrenMap[taskToDelete.id] || {}).items || []"
      @confirm="onDeleteConfirm"
      @close="taskToDelete = null"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { TYPES as TASK_TYPES } from './store'
import { TYPES as TASK_PRIORITY_TYPES } from '../task-priority/store'
import { TYPES as TASK_STATE_TYPES } from '../task-state/store'
import { hasPerm } from '@/components/PermissionHelper'
import { axiosThux } from '@/axiosThux'

import ThuxTableMixin from '@/components/thux-table/ThuxTableMixin'
import ThuxTableChecksMixin from '@/components/thux-table/thux-table-checks/ThuxTableChecksMixin'
import TaskChoicesMixin from './mixins/TaskChoicesMixin'
import TaskTerminateModal from './components/TaskTerminateModal.vue'
import TaskCommentModal from './components/TaskCommentModal.vue'
import TaskOpenSubtasksModal from './components/TaskOpenSubtasksModal.vue'
import TaskDeleteModal from './components/TaskDeleteModal.vue'

const DESCRIPTION_INDENT_BUDGET_PX = 30

export default {
  name: 'TaskHistoryTable',
  mixins: [ThuxTableMixin, ThuxTableChecksMixin, TaskChoicesMixin],
  components: {
    'task-terminate-modal': TaskTerminateModal,
    'task-comment-modal': TaskCommentModal,
    'task-open-subtasks-modal': TaskOpenSubtasksModal,
    'task-delete-modal': TaskDeleteModal
  },
  data () {
    return {
      taskToTerminate: null,
      taskForComments: null,
      taskWithOpenSubtasks: null,
      taskToDelete: null,
      fields: [
        {
          key: 'expander',
          label: '',
          thClass: 'task-tree-expander-col',
          tdClass: 'task-tree-expander-col task-tree-shiftable',
          sortable: false,
          custom: true,
          thStyle: { width: '3%' }
        },
        {
          key: 'category',
          label: this.$t('Category'),
          sortable: true,
          value: 'category_name',
          thStyle: { width: '12%' }
        },
        {
          key: 'name',
          label: this.$t('Name'),
          thClass: '',
          tdClass: 'task-description-col',
          sortable: true,
          custom: true,
          value: 'name',
          thStyle: { width: '25%' }
        },
        {
          key: 'priority',
          label: this.$t('Priority'),
          sortable: true,
          custom: true,
          value: 'priority_name',
          thStyle: { width: '9%' }
        },
        {
          key: 'state',
          label: this.$t('State'),
          sortable: true,
          custom: true,
          value: 'state_name',
          thStyle: { width: '10%' }
        },
        {
          key: 'due_date',
          label: this.$t('Due date'),
          type: 'date',
          thClass: 'text-nowrap',
          tdClass: 'text-nowrap',
          sortable: true,
          value: 'due_date',
          thStyle: { width: '7%' }
        },
        {
          key: 'quotation',
          label: this.$t('Quotation'),
          type: 'text',
          thClass: 'text-nowrap',
          tdClass: 'text-wrap',
          sortable: false,
          custom: true,
          thStyle: { width: '13%' }
        },
        {
          key: 'tags__name',
          label: this.$t('Tags'),
          thClass: 'text-nowrap',
          tdClass: 'text-wrap',
          sortable: true,
          custom: true,
          thStyle: { width: '10%' }
        },
        {
          key: 'comments_count',
          label: this.$t('Comments'),
          thClass: 'text-center text-nowrap',
          tdClass: 'text-center text-nowrap',
          sortable: false,
          custom: true,
          thStyle: { width: '5%' }
        },
        {
          key: 'assignee_names',
          label: this.$t('Assignees'),
          thClass: 'text-nowrap',
          tdClass: 'text-wrap text-uppercase',
          sortable: false,
          value: 'assignee_names',
          thStyle: { width: '13%' }
        },
        {
          key: 'action',
          label: this.$t('Actions'),
          tdClass: 'text-right text-nowrap task-action-col',
          thClass: 'text-right text-nowrap',
          thStyle: { width: '7%' }
        }
      ],
      fieldsColumn: ['expander', 'category', 'name', 'priority', 'state', 'date_create', 'completed_at', 'time_spent'],
      fieldName: 'id',
      expandedSet: [],
      childrenMap: {}
    }
  },
  watch: {
    'params.reloadKey' (val, prev) {
      if (val === prev) return
      // Al reload (es. dopo aver aggiunto una sotto-attività da TaskEdit) NON
      // collassiamo gli alberi aperti: manteniamo lo stato espanso e ri-carichiamo
      // i figli dei rami attualmente aperti. Così un padre già espanso mostra
      // subito la nuova sotto-attività (anche la seconda, la terza, ...) senza
      // dover richiudere e riaprire la riga a mano.
      const expanded = this.expandedSet.slice()
      this.$set(this, 'childrenMap', {})
      expanded.forEach((id) => { this.fetchChildren({ id }) })
    }
  },
  computed: {
    ...mapGetters({
      selectAll: TASK_TYPES.GENERIC.task.task.LIST.GETTERS.selectAll,
      selectedList: TASK_TYPES.GENERIC.task.task.LIST.GETTERS.selectedList,
      priorityList: TASK_PRIORITY_TYPES.GENERIC.task['task-priority'].LIST.GETTERS.list,
      stateList: TASK_STATE_TYPES.GENERIC.task['task-state'].LIST.GETTERS.list
    }),
    priorityById () {
      const map = {}
      if (this.priorityList && this.priorityList.results) {
        this.priorityList.results.forEach((p) => { map[p.id] = p })
      }
      return map
    },
    stateById () {
      const map = {}
      if (this.stateList && this.stateList.results) {
        this.stateList.results.forEach((s) => { map[s.id] = s })
      }
      return map
    },
    showChecks () {
      return false
    },
    showDetailButton () {
      return hasPerm('tasks_task_list')
    },
    isTreeMode () {
      return !!(this.params && this.params.isTreeMode)
    },
    profile () {
      return (this.params && this.params.profile) || 'generic'
    },
    maxTreeDepth () {
      return (this.params && this.params.maxTreeDepth) || 0
    },
    shiftPerLevel () {
      if (!this.isTreeMode || this.maxTreeDepth <= 0) return 0
      return DESCRIPTION_INDENT_BUDGET_PX / this.maxTreeDepth
    },
    dynamicTreeStyles () {
      if (!this.isTreeMode || this.shiftPerLevel <= 0) return ''
      let css = ''
      for (let level = 1; level <= this.maxTreeDepth; level++) {
        const shift = Math.round(level * this.shiftPerLevel)
        css += `.task-history-table tr.task-tree-row-level-${level} > td.task-tree-shiftable{transform:translateX(${shift}px);}`
      }
      return css
    },
    displayList () {
      if (!Array.isArray(this.list)) return []
      const out = []
      const pushNode = (node, level, rootIndex) => {
        out.push(Object.assign({}, node, { _level: level, _rootIndex: rootIndex }))
        if (!this.isTreeMode) return
        if (this.expandedSet.indexOf(node.id) === -1) return
        const entry = this.childrenMap[node.id]
        if (!entry || !entry.loaded) return
        entry.items.forEach((child) => pushNode(child, level + 1, rootIndex))
      }
      this.list.forEach((item, i) => pushNode(item, 0, i))
      return out
    }
  },
  created () {
    if (!this.priorityList || !this.priorityList.results) {
      this.getTaskPriorityList({ filter__status: 1 })
    }
    if (!this.stateList || !this.stateList.results) {
      this.getTaskStateList({ filter__status: 1 })
    }
  },
  methods: {
    ...mapMutations({
      setList: TASK_TYPES.GENERIC.task.task.LIST.MUTATIONS.setList,
      setSelectedList: TASK_TYPES.GENERIC.task.task.LIST.MUTATIONS.setSelectedList,
      setSelectAll: TASK_TYPES.GENERIC.task.task.LIST.MUTATIONS.setSelectAll
    }),
    ...mapActions({
      ...TASK_TYPES.GENERIC.task.task.LIST.ACTIONS,
      markDone: TASK_TYPES.GENERIC.task.task.DETAIL.ACTIONS.markDone,
      setStatus: TASK_TYPES.GENERIC.task.task.DETAIL.ACTIONS.setStatus,
      getTaskPriorityList: TASK_PRIORITY_TYPES.GENERIC.task['task-priority'].LIST.ACTIONS.setFilters,
      getTaskStateList: TASK_STATE_TYPES.GENERIC.task['task-state'].LIST.ACTIONS.setFilters
    }),
    getPriorityChipStyle (item) {
      const priority = this.priorityById[item.priority]
      const color = (priority && priority.color) || '#6c757d'
      return {
        backgroundColor: color,
        color: '#fff'
      }
    },
    getStateChipStyle (item) {
      const state = this.stateById[item.state]
      const color = (state && state.color) || '#6c757d'
      return {
        backgroundColor: color,
        color: '#fff'
      }
    },
    openTaskDetail (id) {
      this.$emit('open-detail-form', id)
    },
    onCommentsClick (item) {
      this.taskForComments = item
    },
    onCreateSubtaskClick (item) {
      this.$emit('open-detail-form', item, true)
    },
    canTerminate (item) {
      if (!item) return false
      const stateCode = this.getStateCode(item.state)
      return stateCode !== 'done' && stateCode !== 'cancelled'
    },
    onDeleteClick (item) {
      this.taskToDelete = item
    },
    onDeleteConfirm (item) {
      this.setStatus({ id: item.id, status: 0 }).then(() => {
        if (item && item.parent && this.childrenMap[item.parent]) {
          this.fetchChildren({ id: item.parent })
        } else {
          this.getList()
        }
      })
    },
    async onTerminateClick (item) {
      if (item.has_open_subtasks) {
        this.taskWithOpenSubtasks = item
        return
      }
      if (item.quotation) {
        this.taskToTerminate = item
      } else {
        try {
          await this.markDone({ id: item.id })
        } catch (e) {
          // errore già notificato da handleError
        }
      }
    },
    onTerminated () {
      const item = this.taskToTerminate
      this.taskToTerminate = null
      if (item && item.parent && this.childrenMap[item.parent]) {
        this.fetchChildren({ id: item.parent })
      }
    },
    tbodyTrClassFn (item) {
      if (!item) return null
      const classes = []
      if (this.isTreeMode) {
        const level = item._level || 0
        if (level > 0) classes.push(`task-tree-row-level-${level}`)
        const rootIdx = typeof item._rootIndex === 'number' ? item._rootIndex : 0
        classes.push(rootIdx % 2 === 0 ? 'task-tree-group-even' : 'task-tree-group-odd')
      }
      return classes.length ? classes.join(' ') : null
    },
    isExpanded (item) {
      return this.expandedSet.indexOf(item.id) !== -1
    },
    isLoading (item) {
      const entry = this.childrenMap[item.id]
      return !!(entry && entry.loading)
    },
    toggleRow (item) {
      const id = item.id
      const idx = this.expandedSet.indexOf(id)
      if (idx !== -1) {
        this.expandedSet.splice(idx, 1)
        return
      }
      // Ricarica sempre i figli a ogni espansione: se una sottoattività è stata
      // aggiunta/modificata mentre la riga era chiusa, la riapertura mostra i dati
      // aggiornati invece della cache stale.
      this.fetchChildren(item).then((ok) => {
        if (ok) this.expandedSet.push(id)
      })
    },
    fetchChildren (item) {
      const id = item.id
      this.$set(this.childrenMap, id, { loading: true, loaded: false, error: false, items: [] })
      // status=1: le sotto-attività cancellate sono soft-deleted e non vanno
      // mostrate nell'albero.
      const url = `/api/v1/task/${this.profile}/visible-task/?parent=${id}&status=1&page_size=1000`
      return axiosThux.get(url)
        .then((response) => {
          const items = (response.data && response.data.results) || []
          this.$set(this.childrenMap, id, { loading: false, loaded: true, error: false, items })
          return true
        })
        .catch((error) => {
          this.$set(this.childrenMap, id, { loading: false, loaded: false, error: true, items: [] })
          const payload = (error && error.response && error.response.data) || { detail: this.$t('Error loading children') }
          this.$store.dispatch('notifyError', { error: payload })
          return false
        })
    }
  }
}
</script>
